<template>
    <FormField id="name" :label="t('connections.edit.fields.name.label')">
        <BaseInput
            ref="nameInput"
            v-model="name"
            autocomplete="off"
            :placeholder="t('connections.edit.fields.requiredPlaceholder')"
            required
            :aria-invalid="isMissing('name')"
            @blur="touched.name = true" />
    </FormField>
    <FormField id="jobTitle" :label="t('connections.edit.fields.jobTitle.label')" :error="jobTitleError">
        <BaseInput ref="jobTitleInput" v-model="jobTitle" :placeholder="t('connections.edit.fields.requiredPlaceholder')" required @blur="touched.jobTitle = true" />
    </FormField>
    <FormField v-if="showEmail" id="email" :label="t('connections.edit.fields.email.label')" :error="errors.alias">
        <BaseInput ref="emailInput" v-model="email" type="email" :placeholder="t('connections.edit.fields.optionalPlaceholder')" />
    </FormField>
</template>

<script setup lang="ts">
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import { computed, reactive, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { showEmail, errors = {} } = defineProps<{ showEmail: boolean; errors?: Record<string, string> }>();
const name = defineModel<string>("name", { required: true });
const jobTitle = defineModel<string>("jobTitle", { required: true });
const email = defineModel<string>("email", { default: "" });

const { t } = useI18n();
const nameInput = useTemplateRef<InstanceType<typeof BaseInput>>("nameInput");
const jobTitleInput = useTemplateRef<InstanceType<typeof BaseInput>>("jobTitleInput");
const emailInput = useTemplateRef<InstanceType<typeof BaseInput>>("emailInput");

// Required fields turn red once the person has left them empty, as :user-invalid did on the raw inputs.
const touched = reactive({ name: false, jobTitle: false });
const values = { name, jobTitle };
const isMissing = (field: keyof typeof touched) => (touched[field] && !values[field].value ? "true" : undefined);
const jobTitleError = computed(() => errors.job_title || (isMissing("jobTitle") ? t("connections.edit.fields.jobTitle.requiredError") : undefined));

// API field errors these fields can show, in focus order.
const errorInputs = { job_title: jobTitleInput, alias: emailInput } as const;
const errorInput = (field: string) => (field in errorInputs ? errorInputs[field as keyof typeof errorInputs].value : null);

function focusFirstError(fields: string[]) {
    const first = Object.keys(errorInputs).find((field) => fields.includes(field) && errorInput(field));
    if (first) {
        errorInput(first)?.focus();
    }
}

defineExpose({ focus: () => nameInput.value?.focus(), showsError: (field: string) => !!errorInput(field), focusFirstError });
</script>
